import { get } from '@ember/object';
import changesetGet from 'ember-changeset/helpers/changeset-get';
import ScaleSurveyEditionFormComponent from 'parog-web/components/abstract/scale-survey-edition-form';
import type { Payload } from 'parog-web/components/forc/abstract/journal-event-based-component';
import Provider from 'parog-web/components/forc/provider';
import BdiIiDeclaredJournalEvent from 'parog-web/models/bdi-ii-declared-journal-event';
import bdiIiScoreDescription from 'parog-web/utils/bdi-ii-score-description';
import TwoButtonTabs from 'ui-kit/components/two-button-tabs';

export default class BdiIiEditionFormComponent extends ScaleSurveyEditionFormComponent {
  static override forcDisplayName =
    "Questionnaire d'évaluation de la dépression - BDI-II";
  static override fullComponentId = 'scale-surveys/bdi-ii-edition-form';
  static override journalEventClass = BdiIiDeclaredJournalEvent;

  override get maximalScore(): ScaleSurveyEditionFormComponent['maximalScore'] {
    return 63;
  }

  override get numberOfItems(): ScaleSurveyEditionFormComponent['numberOfItems'] {
    return 21;
  }

  override _computeScore(
    payload: Payload
  ): ScaleSurveyEditionFormComponent['score'] {
    const number = this.questionIds.reduce(
      (total, index) => total + parseInt(get(payload, index) as string),
      0
    );

    return {
      number,
      description: bdiIiScoreDescription(number)
    };
  }

  <template>
    <Provider @forcComponent={{this}}>
      <:content as |Form|>
        <TwoButtonTabs
          class='large'
          @selected={{if
            (changesetGet this.changeset 'isTotalScoreFilled')
            'second'
            'first'
          }}
          @onClick={{this.onTabButtonClicked}}
        >
          <:firstButton>Répondre aux questions</:firstButton>
          <:secondButton>Renseigner le score</:secondButton>

          <:firstTab>
            <div class='field'>
              <Form.Date
                @labelName='Date à laquelle le questionnaire a été renseigné'
                @propertyName='occurredAt'
              />
            </div>

            <h4>Ce questionnaire comporte 21 groupes d'énoncés. Veuillez lire
              avec soin chacun de ces groupes puis, dans chaque groupe,
              choisissez l'énoncé qui décrit le mieux comment vous vous êtes
              senti(e) au cours des deux dernières semaines, incluant
              aujourd'hui. Si, dans un groupe d'énoncés, vous en trouvez
              plusieurs qui semblent décrire également bien ce que vous
              ressentez, choisissez celui qui a le chiffre le plus élevé.</h4>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='1. Tristesse'
                @propertyName='0'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label='Je ne me sens pas triste.'
                />
                <Group.FieldRadio
                  @value='1'
                  @label='Je me sens très souvent triste.'
                />
                <Group.FieldRadio
                  @value='2'
                  @label='Je suis tout le temps triste.'
                />
                <Group.FieldRadio
                  @value='3'
                  @label="Je suis si triste ou si malheureux(se) que ce n'est pas supportable."
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='2. Pessimisme'
                @propertyName='1'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label='Je ne suis pas découragé(e) face à mon avenir.'
                />
                <Group.FieldRadio
                  @value='1'
                  @label="Je me sens plus découragé(e) qu'avant face à mon avenir."
                />
                <Group.FieldRadio
                  @value='2'
                  @label="Je ne m'attends pas à ce que les choses s'arrangent pour moi."
                />
                <Group.FieldRadio
                  @value='3'
                  @label="J'ai le sentiment que mon avenir est sans espoir et qu'il ne peut qu'empirer."
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='3. Échecs dans le passé'
                @propertyName='2'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Je n'ai pas le sentiment d'avoir échoué dans la vie, d'être un(e) raté(e)."
                />
                <Group.FieldRadio
                  @value='1'
                  @label="J'ai échoué plus souvent que je n'aurais dû."
                />
                <Group.FieldRadio
                  @value='2'
                  @label="Quand je pense à mon passé, je constate un grand nombre d'échecs."
                />
                <Group.FieldRadio
                  @value='3'
                  @label="J'ai le sentiment d'avoir complètement raté ma vie."
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='4. Perte de plaisir'
                @propertyName='3'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="J'éprouve toujours autant de plaisir qu'avant aux choses qui me plaisent."
                />
                <Group.FieldRadio
                  @value='1'
                  @label="Je n'éprouve pas autant de plaisir aux choses qu'avant."
                />
                <Group.FieldRadio
                  @value='2'
                  @label="J'éprouve très peu de plaisir aux choses qui me plaisaient habituellement."
                />
                <Group.FieldRadio
                  @value='3'
                  @label="Je n'éprouve aucun plaisir aux choses qui me plaisaient habituellement."
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='5. Sentiments de culpabilité'
                @propertyName='4'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label='Je ne me sens pas particulièrement coupable.'
                />
                <Group.FieldRadio
                  @value='1'
                  @label="Je me sens coupable pour bien des choses que j'ai faites ou que j'aurais dû faire."
                />
                <Group.FieldRadio
                  @value='2'
                  @label='Je me sens coupable la plupart du temps.'
                />
                <Group.FieldRadio
                  @value='3'
                  @label='Je me sens tout le temps coupable.'
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title="6. Sentiment d'être puni(e)"
                @propertyName='5'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Je n'ai pas le sentiment d'être puni(e)."
                />
                <Group.FieldRadio
                  @value='1'
                  @label='Je sens que je pourrais être puni(e).'
                />
                <Group.FieldRadio
                  @value='2'
                  @label="Je m'attends à être puni(e)."
                />
                <Group.FieldRadio
                  @value='3'
                  @label="J'ai le sentiment d'être puni(e)."
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='7. Sentiments négatifs envers soi-même'
                @propertyName='6'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Mes sentiments envers moi-même n'ont pas changé."
                />
                <Group.FieldRadio
                  @value='1'
                  @label="J'ai perdu confiance en moi."
                />
                <Group.FieldRadio
                  @value='2'
                  @label='Je suis déçu(e) par moi-même.'
                />
                <Group.FieldRadio
                  @value='3'
                  @label="Je ne m'aime pas du tout."
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='8. Attitude critique envers soi'
                @propertyName='7'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Je ne me blâme pas ou ne me critique pas plus que d'habitude."
                />
                <Group.FieldRadio
                  @value='1'
                  @label="Je suis plus critique envers moi-même que je ne l'étais."
                />
                <Group.FieldRadio
                  @value='2'
                  @label='Je me reproche tous mes défauts.'
                />
                <Group.FieldRadio
                  @value='3'
                  @label='Je me reproche tous les malheurs qui arrivent.'
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='9. Pensées ou désirs de suicide'
                @propertyName='8'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label='Je ne pense pas du tout à me suicider.'
                />
                <Group.FieldRadio
                  @value='1'
                  @label="Il m'arrive de penser à me suicider, mais je ne le ferai pas."
                />
                <Group.FieldRadio @value='2' @label="J'aimerais me suicider." />
                <Group.FieldRadio
                  @value='3'
                  @label="Je me suiciderais si l'occasion se présentait."
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='10. Pleurs'
                @propertyName='9'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Je ne pleure pas plus qu'avant."
                />
                <Group.FieldRadio
                  @value='1'
                  @label="Je pleure plus qu'avant."
                />
                <Group.FieldRadio
                  @value='2'
                  @label='Je pleure pour la moindre petite chose.'
                />
                <Group.FieldRadio
                  @value='3'
                  @label='Je voudrais pleurer mais je ne suis pas capable.'
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='11. Agitation'
                @propertyName='10'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Je ne suis pas plus agité(e) ou plus tendu(e) que d'habitude."
                />
                <Group.FieldRadio
                  @value='1'
                  @label="Je me sens plus agité(e) ou plus tendu(e) que d'habitude."
                />
                <Group.FieldRadio
                  @value='2'
                  @label="Je suis si agité(e) ou tendu(e) que j'ai du mal à rester tranquille."
                />
                <Group.FieldRadio
                  @value='3'
                  @label='Je suis si agité(e) ou tendu(e) que je dois continuellement bouger ou faire quelque chose.'
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title="12. Perte d'intérêt"
                @propertyName='11'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Je n'ai pas perdu d'intérêt pour les gens ou pour les activités."
                />
                <Group.FieldRadio
                  @value='1'
                  @label="Je m'intéresse moins qu'avant aux gens et aux choses."
                />
                <Group.FieldRadio
                  @value='2'
                  @label="Je ne m'intéresse presque plus aux gens et aux choses."
                />
                <Group.FieldRadio
                  @value='3'
                  @label="J'ai du mal à m'intéresser à quoique ce soit."
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='13. Indécision'
                @propertyName='12'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Je prends des décisions toujours aussi bien qu'avant."
                />
                <Group.FieldRadio
                  @value='1'
                  @label="Il m'est plus difficile que d'habitude de prendre des décisions."
                />
                <Group.FieldRadio
                  @value='2'
                  @label="J'ai beaucoup plus de mal qu'avant à prendre des décisions."
                />
                <Group.FieldRadio
                  @value='3'
                  @label="J'ai du mal à prendre n'importe quelle décision."
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='14. Dévalorisation'
                @propertyName='13'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Je pense être quelqu'un de valable."
                />
                <Group.FieldRadio
                  @value='1'
                  @label="Je ne crois pas avoir autant de valeur ni être aussi utile qu'avant."
                />
                <Group.FieldRadio
                  @value='2'
                  @label='Je me sens moins valable que les autres.'
                />
                <Group.FieldRadio
                  @value='3'
                  @label='Je sens que je ne vaux absolument rien.'
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title="15. Perte d'énergie"
                @propertyName='14'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="J'ai toujours autant d'énergie qu'avant."
                />
                <Group.FieldRadio
                  @value='1'
                  @label="J'ai moins d'énergie qu'avant."
                />
                <Group.FieldRadio
                  @value='2'
                  @label="Je n'ai pas assez d'énergie pour pouvoir faire grand-chose."
                />
                <Group.FieldRadio
                  @value='3'
                  @label="J'ai trop peu d'énergie pour faire quoi que ce soit."
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='16. Modifications dans les habitudes de sommeil'
                @propertyName='15'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Mes habitudes de sommeil n'ont pas changé."
                />
                <Group.FieldRadio
                  @value='1a'
                  @label="Je dors un peu plus que d'habitude."
                />
                <Group.FieldRadio
                  @value='1b'
                  @label="Je dors un peu moins que d'habitude."
                />
                <Group.FieldRadio
                  @value='2a'
                  @label="Je dors beaucoup plus que d'habitude."
                />
                <Group.FieldRadio
                  @value='2b'
                  @label="Je dors beaucoup moins que d'habitude."
                />
                <Group.FieldRadio
                  @value='3a'
                  @label='Je dors presque toute la journée.'
                />
                <Group.FieldRadio
                  @value='3b'
                  @label='Je me réveille une ou deux heures plus tôt et je suis incapable de me rendormir.'
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='17. Irritabilité'
                @propertyName='16'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Je ne suis pas plus irritable que d'habitude."
                />
                <Group.FieldRadio
                  @value='1'
                  @label="Je suis plus irritable que d'habitude."
                />
                <Group.FieldRadio
                  @value='2'
                  @label="Je suis beaucoup plus irritable que d'habitude."
                />
                <Group.FieldRadio
                  @value='3'
                  @label='Je suis constamment irritable.'
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title="18. Modifications de l'appétit"
                @propertyName='17'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Mon appétit n'a pas changé."
                />
                <Group.FieldRadio
                  @value='1a'
                  @label="J'ai un peu moins d'appétit que d'habitude."
                />
                <Group.FieldRadio
                  @value='1b'
                  @label="J'ai un peu plus d'appétit que d'habitude."
                />
                <Group.FieldRadio
                  @value='2a'
                  @label="J'ai beaucoup moins d'appétit que d'habitude."
                />
                <Group.FieldRadio
                  @value='2b'
                  @label="J'ai beaucoup plus d'appétit que d'habitude."
                />
                <Group.FieldRadio
                  @value='3a'
                  @label="Je n'ai pas d'appétit du tout."
                />
                <Group.FieldRadio
                  @value='3b'
                  @label="J'ai constamment envie de manger."
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='19. Difficulté à se concentrer'
                @propertyName='18'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Je parviens à me concentrer toujours aussi bien qu'avant."
                />
                <Group.FieldRadio
                  @value='1'
                  @label="Je ne parviens pas à me concentrer aussi bien que d'habitude."
                />
                <Group.FieldRadio
                  @value='2'
                  @label="J'ai du mal à me concentrer longtemps sur quoi que ce soit."
                />
                <Group.FieldRadio
                  @value='3'
                  @label='Je me trouve incapable de me concentrer sur quoi que ce soit.'
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title='20. Fatigue'
                @propertyName='19'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Je ne suis pas plus fatiqué(e) que d'habitude."
                />
                <Group.FieldRadio
                  @value='1'
                  @label="Je me fatigue plus facilement que d'habitude."
                />
                <Group.FieldRadio
                  @value='2'
                  @label='Je suis trop fatigué(e) pour faire un grand nombre de choses que je faisais avant.'
                />
                <Group.FieldRadio
                  @value='3'
                  @label='Je suis trop fatigué(e) pour faire la plupart des choses que je faisais avant.'
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='field'>
              <Form.FieldsRadioGroup
                @title="21. Perte d'intérêt pour le sexe"
                @propertyName='20'
                as |Group|
              >
                <Group.FieldRadio
                  @value='0'
                  @label="Je n'ai pas noté de changement récent dans mon intérêt pour le sexe."
                />
                <Group.FieldRadio
                  @value='1'
                  @label="Le sexe m'intéresse moins qu'avant."
                />
                <Group.FieldRadio
                  @value='2'
                  @label="Le sexe m'intéresse beaucoup moins maintenant."
                />
                <Group.FieldRadio
                  @value='3'
                  @label="J'ai perdu tout intérêt pour le sexe."
                />
              </Form.FieldsRadioGroup>
            </div>

            <div class='ui message' data-test-score>
              <strong>Score : </strong>
              {{#if this.isChangesetValid}}
                {{this.score.number}}
                –
                {{this.score.description}}
              {{else}}
                veuillez remplir tous les champs afin d'obtenir le score.
              {{/if}}
            </div>
          </:firstTab>

          <:secondTab>
            <div class='field'>
              <Form.Date
                @labelName='Date à laquelle le questionnaire a été renseigné'
                @propertyName='occurredAt'
              />
            </div>
            <div class='field'>
              <Form.NumberInput
                @labelName='Score total'
                @propertyName='total'
                @type='integer'
              />
            </div>
          </:secondTab>
        </TwoButtonTabs>
      </:content>
    </Provider>
  </template>
}
